/**
 * The red number (Derek Knost, 2026-10-14: "a red number on the app like Outlook"): the bell's unread
 * count in the tab title ("(3) Arevina") and on the installed app's icon where the browser supports the
 * Badging API. Pages that set their own title keep it; the count is put back in front of it.
 */
import { useEffect } from 'react';
import { useUnreadCounts } from '../../context/UnreadCountsContext';
import { useAuthCtx } from '../../context/AuthContext';
import { applyUnreadBadge } from '../../lib/notifications/push';

export default function UnreadBadgeSync() {
  const { notifications } = useUnreadCounts();
  const { user } = useAuthCtx();
  const count = user ? notifications : 0;

  useEffect(() => {
    const apply = () => {
      const next = applyUnreadBadge(count, document.title);
      if (next !== document.title) document.title = next;
    };
    apply();
    // A page that sets its own title (Insights, a job page) gets the count put back in front.
    const el = document.querySelector('title');
    if (!el || typeof MutationObserver === 'undefined') return;
    const mo = new MutationObserver(() => {
      const next = applyUnreadBadge(count, document.title);
      if (next !== document.title) document.title = next;
    });
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return () => mo.disconnect();
  }, [count]);

  // A tapped push while a tab is open: the service worker asks this tab to open the notice's page.
  useEffect(() => {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; href?: string } | null;
      if (d?.type === 'arevina-open' && typeof d.href === 'string' && d.href.startsWith('/') && !d.href.startsWith('//')) window.location.assign(d.href);
    };
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, []);

  return null;
}
